import React from "react";
import { Button, Tooltip } from "antd";
import { QuestionCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useGuideOptional } from "../context/GuideContext";
import type { GuideId } from "../types";

interface PageTourButtonProps {
  /** Tour ID to start */
  tourId: GuideId;
  /** Button size */
  size?: "small" | "middle" | "large";
  /** Show as icon only (tooltip will show text) */
  iconOnly?: boolean;
  /** Custom button style */
  style?: React.CSSProperties;
}

/**
 * PageTourButton - Button to start a tour from a specific page
 * Can be placed in page headers for easy access to tours
 */
export const PageTourButton: React.FC<PageTourButtonProps> = ({
  tourId,
  size = "small",
  iconOnly = false,
  style,
}) => {
  const { t } = useTranslation("guide");
  const guideContext = useGuideOptional();

  // If guide context is not available, don't render
  if (!guideContext) {
    return null;
  }

  const { startTour, isGuideComplete } = guideContext;
  const isComplete = isGuideComplete(tourId);

  const handleClick = () => {
    startTour(tourId);
  };

  const buttonText = isComplete
    ? t("actions.restartTour", "Replay")
    : t("actions.startTour", "Start");

  const tooltipText = isComplete
    ? t("pageTour.replayTooltip", "Replay guide for this page")
    : t("pageTour.startTooltip", "View guide for this page");

  if (iconOnly) {
    return (
      <Tooltip title={tooltipText}>
        <Button
          type={isComplete ? "text" : "primary"}
          ghost={!isComplete}
          size={size}
          icon={
            isComplete ? <QuestionCircleOutlined /> : <PlayCircleOutlined />
          }
          onClick={handleClick}
          style={style}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipText}>
      <Button
        type={isComplete ? "default" : "primary"}
        size={size}
        icon={<PlayCircleOutlined />}
        onClick={handleClick}
        style={style}
      >
        {buttonText}
      </Button>
    </Tooltip>
  );
};
